// ============================================================================
// HeadcountTab.jsx
// Heads by cost centre, with each row opening onto that cost centre's own
// history and the people behind the number.
//
// The company total moving by +54 is not actionable on its own — the question
// it provokes is always "where". The movement table answers that for the last
// month; expanding a row answers it for that cost centre across every month
// loaded, which is the same chart the top of the page draws for the company.
//
// Sorting and the cost-center focus filter are both client-side: a reader
// scoped to several cost centres already has every row they are allowed to
// see in `data.cost_centers`, so there is nothing to ask the server for.
// ============================================================================

import { Fragment, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronRight, ChevronDown } from 'lucide-react';
import { api } from '../../services/api';
import { c, t, r, card, tbl } from '../../theme';
import LoadingSpinner from '../Shared/LoadingSpinner';
import HeadcountTrend from './HeadcountTrend';
import MultiSelect from '../ContactCenter/MultiSelect';
import { useSort, sortRows, SortTh } from '../Shared/sortable';

const n0 = (v) => Number(v ?? 0).toLocaleString();
const n2 = (v) => Number(v ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// Share of the total on THIS page, not of the company. For a reader scoped to
// a few cost centres the endpoint returns only those, and total_heads is the
// sum of exactly the rows below — so the column adds to 100% and its
// denominator is the figure printed in the Total row, which is the only one
// they can check. A company-wide denominator would be unverifiable from the
// screen and would not add up. Focusing on a subset narrows that denominator
// the same way scoping already does, for the same reason.
const pct = (heads, total) => (total > 0 ? (heads / total) * 100 : 0);
// One decimal: at ~2,700 heads a single person is 0.04%, so more precision
// implies a resolution the number does not have, and less makes every small
// cost centre read as 0%.
const n1pct = (v) => `${v.toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`;

export default function HeadcountTab({ data, expanded, onToggle }) {
  const allRows = data?.cost_centers || [];
  const toggle = onToggle;

  // Focus: narrows which cost centres are shown, same MultiSelect the Contact
  // Center dashboards already use. Empty selection means "all" — a reader
  // with one cost centre never sees this control do anything interesting,
  // which is fine; it is for the reader who holds several and wants to look
  // at a handful at a time.
  const [focusedIds, setFocusedIds] = useState([]);
  // By code, not by name — sorting names alphabetically scatters each
  // facility block (500 SBC Corporate, 542 29 Palms Transfer Station, ...)
  // across the whole list even though the leading code already groups them.
  const ccRowsSorted = [...allRows].sort((a, b) => (parseInt(a.raw_cost_center, 10) || 0) - (parseInt(b.raw_cost_center, 10) || 0));
  const ccOptions = ccRowsSorted.map((row) => row.cost_center_id || row.raw_cost_center);
  const ccNameById = new Map(allRows.map((row) => [row.cost_center_id || row.raw_cost_center, row.raw_cost_center]));
  const focusedSet = new Set(focusedIds);
  const rows = focusedIds.length
    ? allRows.filter((row) => focusedSet.has(row.cost_center_id || row.raw_cost_center))
    : allRows;

  const totalHeads = focusedIds.length
    ? rows.reduce((a, row) => a + Number(row.heads || 0), 0)
    : Number(data?.total_heads ?? 0);

  const sort = useSort();
  const sorted = sortRows(rows, sort.key, sort.dir, {
    name:     (r0) => r0.raw_cost_center || '',
    heads:    (r0) => Number(r0.heads || 0),
    pct:      (r0) => pct(Number(r0.heads || 0), totalHeads),
    on_leave: (r0) => Number(r0.on_leave || 0),
    fte:      (r0) => Number(r0.fte || 0),
  });

  return (
    <div>
      {allRows.length > 1 && (
        <div style={{ marginBottom: 10 }}>
          <MultiSelect
            label="Cost Centers" options={ccOptions} selected={focusedIds} onChange={setFocusedIds}
            allLabel="All" noun="focused" emptyMeansAll align="left"
            displayFn={(id) => ccNameById.get(id) || id}
          />
        </div>
      )}
      <div style={{ ...card.base, overflow: 'auto', maxHeight: 640 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={{ ...tbl.th, width: 28, position: 'sticky', top: 0, zIndex: 1 }} />
              <SortTh label="Cost Center" sortKey="name" sort={sort} sticky />
              <SortTh label="Heads" sortKey="heads" sort={sort} align="right" sticky />
              <SortTh label="% of Total" sortKey="pct" sort={sort} align="right" sticky />
              <SortTh label="On Leave" sortKey="on_leave" sort={sort} align="right" sticky />
              <SortTh label="FTE" sortKey="fte" sort={sort} align="right" sticky />
            </tr>
          </thead>
          <tbody>
            {sorted.map((row) => {
              const id = row.cost_center_id || row.raw_cost_center;
              const open = expanded.has(id);
              return (
                <Fragment key={id}>
                  <tr id={`cc-${id}`}
                      style={{ ...tbl.trHover, background: open ? c.infoTint : undefined }}
                      onClick={() => toggle(id)}>
                    <td style={{ ...tbl.td, color: c.textMuted }}>
                      {open ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
                    </td>
                    <td style={tbl.td}>{row.raw_cost_center}</td>
                    <td style={{ ...tbl.tdR, fontWeight: open ? 600 : 400 }}>{n0(row.heads)}</td>
                    <td style={{ ...tbl.tdR, color: c.textMuted }}>{n1pct(pct(row.heads, totalHeads))}</td>
                    <td style={{ ...tbl.tdR, color: c.textMuted }}>{row.on_leave ? n0(row.on_leave) : '—'}</td>
                    <td style={{ ...tbl.tdR, color: c.textMuted }}>{n2(row.fte)}</td>
                  </tr>
                  {open && (
                    <tr>
                      <td colSpan={6} style={{ padding: 0, background: c.surfaceSunk, borderBottom: `1px solid ${c.border}` }}>
                        <CostCenterPanel ccId={row.cost_center_id} name={row.raw_cost_center} />
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
            {!sorted.length && (
              <tr><td colSpan={6} style={{ ...tbl.td, textAlign: 'center', color: c.textFaint, padding: 24 }}>
                No cost centers match the current focus.
              </td></tr>
            )}
          </tbody>
          <tfoot>
            <tr style={{ background: c.surfaceSunk, fontWeight: 600 }}>
              <td style={tbl.td} />
              <td style={tbl.td}>
                Total{focusedIds.length ? ` (${rows.length} of ${allRows.length})` : ''}
              </td>
              <td style={tbl.tdR}>{n0(totalHeads)}</td>
              <td style={tbl.tdR}>{totalHeads > 0 ? '100.0%' : '—'}</td>
              <td style={tbl.tdR} /><td style={tbl.tdR} />
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}

function CostCenterPanel({ ccId, name }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['hr-headcount-detail', ccId],
    queryFn: () => api.get(`/hr-headcount/headcount/${ccId}`),
    enabled: !!ccId,
  });
  const sort = useSort('worker_name');

  if (!ccId) {
    return <div style={{ padding: 14, ...t.bodySm, color: c.textMuted }}>
      This cost center is not mapped in the portal, so its history and people cannot be listed.
    </div>;
  }

  const workers = sortRows(data?.workers || [], sort.key, sort.dir, {
    worker_name: (w) => w.worker_name || '',
    job_profile: (w) => w.job_profile || '',
    raw_location: (w) => w.raw_location || '',
    manager:     (w) => w.manager || '',
  });

  return (
    <div style={{ padding: '12px 16px 16px 44px' }}>
      {/* Same chart the page draws for the company, scoped to one cost centre.
          With a single month loaded it says so rather than drawing a line
          through one point. */}
      <HeadcountTrend ccId={ccId} ccName={name} />

      {isLoading && <LoadingSpinner />}
      {error && <div style={{ ...t.bodySm, color: c.dangerText }}>{error.message}</div>}

      {data && data.names_visible === false && (
        <div style={{ ...t.bodySm, color: c.textMuted }}>
          {n0(data.heads)} heads here. You do not have permission to see individual
          worker names for this cost center.
        </div>
      )}

      {data?.workers?.length > 0 && (
        <>
          <div style={{ ...t.label, color: c.textStrong, margin: '4px 0 6px' }}>
            People <span style={{ ...t.micro, color: c.textFaint, fontWeight: 400 }}>({n0(data.workers.length)})</span>
          </div>
          <div style={{ maxHeight: 560, overflowY: 'auto', border: `1px solid ${c.border}`,
                        borderRadius: r.md, background: c.surface }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
              <colgroup>
                <col style={{ width: '32%' }} />
                <col style={{ width: '24%' }} />
                <col style={{ width: '20%' }} />
                <col style={{ width: '24%' }} />
              </colgroup>
              <thead>
                <tr>
                  <SortTh label="Worker" sortKey="worker_name" sort={sort} sticky />
                  <SortTh label="Job Profile" sortKey="job_profile" sort={sort} sticky />
                  <SortTh label="Location" sortKey="raw_location" sort={sort} sticky />
                  <SortTh label="Manager" sortKey="manager" sort={sort} sticky />
                </tr>
              </thead>
              <tbody>
                {workers.map((w, i) => (
                  <tr key={`${w.employee_id}-${i}`} style={tbl.tr}>
                    <td style={tbl.tdSm}>
                      {w.worker_name}
                      {w.worker_status === 'on_leave' && (
                        <span style={{ marginLeft: 6, ...t.micro, color: c.warningText }}>on leave</span>
                      )}
                      {w.is_manual_addition && (
                        <span title="Not in Workday's roster extract — added by hand to correct an understated headcount"
                              style={{ marginLeft: 6, ...t.micro, color: c.warningText }}>manual</span>
                      )}
                      <div style={{ ...t.micro, color: c.textFaint }}>{w.employee_id}</div>
                    </td>
                    <td style={{ ...tbl.tdSm, color: c.textMuted }}>{w.job_profile || '—'}</td>
                    <td style={{ ...tbl.tdSm, color: c.textMuted }}>{w.raw_location || '—'}</td>
                    <td style={{ ...tbl.tdSm, color: c.textMuted }}>{w.manager || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
